import { useEffect, useRef } from "react";

/**
 * Maps Shift+wheel (or primarily horizontal wheel) to horizontal scroll.
 * Leaves normal vertical wheel/page scroll alone so the document can still scroll.
 */
export const useHorizontalWheelScroll = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const onWheel = (event: WheelEvent) => {
      if (element.scrollWidth <= element.clientWidth) return;

      const mostlyHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      if (!event.shiftKey && !mostlyHorizontal) return;

      const delta =
        event.shiftKey && Math.abs(event.deltaY) >= Math.abs(event.deltaX)
          ? event.deltaY
          : mostlyHorizontal
            ? event.deltaX
            : event.deltaY;
      if (delta === 0) return;

      const maxScroll = element.scrollWidth - element.clientWidth;
      const nextLeft = Math.max(0, Math.min(maxScroll, element.scrollLeft + delta));
      if (nextLeft === element.scrollLeft) return;

      element.scrollLeft = nextLeft;
      event.preventDefault();
    };

    element.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", onWheel);
    };
  }, []);

  return ref;
};
